import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed to updates.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe.");
    }
  };
  return (
    <footer data-testid="site-footer" className="bg-[#0B0F19] text-slate-300 border-t border-white/10 relative overflow-hidden">
      <div className="ici-grid-bg-dark absolute inset-0 opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 relative">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-md bg-[#0055FF] flex items-center justify-center">
                <span className="font-display text-white text-lg">IC</span>
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-display text-white text-lg">InterCorp Industries</span>
                <span className="text-[9px] font-mono text-slate-400 tracking-[0.3em] uppercase">Automotive Supply · USA</span>
              </div>
            </Link>
            <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
              Precision fuel delivery components for domestic, European, and Asian vehicles. Nationwide shipping, clear
              product information, dependable service.
            </p>
            <form onSubmit={submit} className="mt-6 flex gap-2 max-w-md">
              <Input
                data-testid="footer-newsletter-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/5 border-white/10 text-slate-100"
              />
              <Button data-testid="footer-newsletter-submit" type="submit" className="bg-[#0055FF] hover:bg-[#0044CC]">Subscribe</Button>
            </form>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white mb-4 text-sm tracking-wide uppercase">Shop</h4>
            <ul className="space-y-2 text-sm">
              <li><Link className="hover:text-white" to="/shop">All products</Link></li>
              <li><Link className="hover:text-white" to="/shop/Fuel%20Injector%20Sets">Injector sets</Link></li>
              <li><Link className="hover:text-white" to="/shop/Single%20Fuel%20Injectors">Single injectors</Link></li>
              <li><Link className="hover:text-white" to="/shop/OEM%20Fuel%20Injectors">OEM injectors</Link></li>
              <li><Link className="hover:text-white" to="/shop/Remanufactured%20Injectors">Remanufactured</Link></li>
              <li><Link className="hover:text-white" to="/shop/Automotive%20Tools">Automotive tools</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white mb-4 text-sm tracking-wide uppercase">Help</h4>
            <ul className="space-y-2 text-sm">
              <li><Link className="hover:text-white" to="/fitment-assistance">Fitment assistance</Link></li>
              <li><Link className="hover:text-white" to="/request-a-part">Request a part</Link></li>
              <li><Link className="hover:text-white" to="/vehicle-finder">Vehicle finder</Link></li>
              <li><Link className="hover:text-white" to="/search/part">Search by part #</Link></li>
              <li><Link className="hover:text-white" to="/contact">Contact us</Link></li>
              <li><Link className="hover:text-white" to="/faq">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white mb-4 text-sm tracking-wide uppercase">Policies</h4>
            <ul className="space-y-2 text-sm">
              <li><Link className="hover:text-white" to="/policies/shipping">Shipping</Link></li>
              <li><Link className="hover:text-white" to="/policies/returns">Returns</Link></li>
              <li><Link className="hover:text-white" to="/policies/warranty">Warranty</Link></li>
              <li><Link className="hover:text-white" to="/policies/privacy">Privacy</Link></li>
              <li><Link className="hover:text-white" to="/policies/terms">Terms</Link></li>
              <li><Link className="hover:text-white" to="/policies/accessibility">Accessibility</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white mb-4 text-sm tracking-wide uppercase">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link className="hover:text-white" to="/about">About</Link></li>
              <li><Link className="hover:text-white" to="/wholesale">Wholesale</Link></li>
              <li><Link className="hover:text-white" to="/knowledge">Knowledge Center</Link></li>
              <li>
                <a className="hover:text-white flex items-center gap-1" href="https://www.ebay.com/usr/intercorp_industries" target="_blank" rel="noopener noreferrer">
                  eBay store <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} InterCorp Industries · Nationwide automotive parts supply</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-mono">
            <a data-testid="footer-phone" href="tel:+16267103581" className="flex items-center gap-1.5 text-slate-300 hover:text-white">
              <Phone className="w-3.5 h-3.5" /> (626) 710-3581
            </a>
            <a data-testid="footer-email" href="mailto:intercorpindustries1@gmail.com" className="flex items-center gap-1.5 text-slate-300 hover:text-white">
              <Mail className="w-3.5 h-3.5" /> intercorpindustries1@gmail.com
            </a>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> United States</span>
            <span>Mon–Fri 9am–6pm ET</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
